import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { AppShell, MenuButton, type NavItem } from '@wodi/ui';
import { useCallback, useEffect } from 'react';

import { api } from '../api/client.ts';
import { useI18n } from '../i18n/i18n.tsx';
import { translatePageTitle } from '../i18n/titles.ts';
import { applyAppearance } from '../appearance/apply.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useIdle } from '../hooks/use-idle.ts';
import { clearAllDrafts } from '../patients/drafts.ts';
import { RegisterNewPatientButton } from '../patients/register-new-patient-button.tsx';
import { PatientSearchPalette } from '../patients/patient-search-palette.tsx';
import { OfflineStrips, RetryQueueRunner } from '../offline/offline-status.tsx';
import { retryQueue } from '../offline/retry-queue.ts';
import { useGuardedSignOut } from '../offline/sign-out-guard.tsx';
import { canSeeReports } from '../reports/catalogue.ts';
import { toasts } from '../toasts.ts';
import { NotificationStrips } from '../lab/notifications.tsx';
import { MyLicenceStrip } from '../staff/licence-strip.tsx';
import { settingsAreasFor } from './settings-overview.tsx';
import { useSignOut } from './use-sign-out.ts';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

export function AppLayout() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const { requestSignOut, dialog: signOutDialog } = useGuardedSignOut(signOut);
  const { t, language, setLanguage } = useI18n();

  // The signed-in person's language wins over what this device used last.
  const sessionLanguage = session?.language;
  useEffect(() => {
    if (sessionLanguage !== undefined && sessionLanguage !== language) setLanguage(sessionLanguage);
  }, [sessionLanguage, language, setLanguage]);

  const onIdle = useCallback(() => {
    // Everything saved on this device goes with the session (ADR 0018); say so if any of it
    // had not reached Wodi yet.
    const unsent = retryQueue.snapshot().length > 0;
    queryClient.clear();
    void clearAllDrafts();
    void navigate({
      to: '/sign-in',
      search: { reason: unsent ? 'expired_unsent' : 'expired', next: location.href },
    });
  }, [queryClient, navigate, location.href]);
  useIdle(session?.idleMinutes ?? 15, onIdle);

  // The signed-in person's theme and mode win over what this device used last (ADR 0030).
  const theme = session?.appearance.theme;
  const mode = session?.appearance.mode;
  useEffect(() => {
    if (theme !== undefined && mode !== undefined) applyAppearance({ theme, mode });
  }, [theme, mode]);

  const switchFacility = useMutation({
    mutationFn: (facilityId: string) => api('POST', '/auth/facility', { body: { facilityId } }),
    onSuccess: async (_data, facilityId) => {
      await queryClient.invalidateQueries();
      const name = session?.facilities.find((f) => f.id === facilityId)?.name;
      toasts.show({
        title: name === undefined ? t('shell.facilityChanged') : t('shell.nowWorkingAt', { name }),
      });
    },
  });

  if (session === null) return <Navigate to="/sign-in" />;

  // Grouped by where the work happens (ADR 0019). Each person sees only what they may open;
  // a group with nothing in it is not shown.
  const navItems: NavItem[] = [
    { href: '/', label: t('nav.home'), icon: 'home' },
    ...(can(session, 'visits.open')
      ? [
          {
            href: '/reception',
            label: t('nav.reception'),
            icon: 'reception',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    ...(can(session, 'patients.merge.approve')
      ? [
          {
            href: '/patient-merges',
            label: t('nav.recordMerges'),
            icon: 'approvals',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    ...(can(session, 'appointments.read')
      ? [
          {
            href: '/appointments',
            label: t('nav.appointments'),
            icon: 'appointments',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    // Medical records and data-subject requests (docs/plans/phase2-records-data-rights.md §6).
    ...(can(session, 'records.requests.read')
      ? [
          {
            href: '/records',
            label: t('nav.records'),
            icon: 'records',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    // Paper charts out of the records room (docs/plans/phase2-medical-records-charts.md §6).
    ...(can(session, 'records.charts.read')
      ? [
          {
            href: '/charts',
            label: t('nav.charts'),
            icon: 'records',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    // Referrals sent and received (docs/plans/phase2-referrals.md §6).
    ...(can(session, 'referrals.read')
      ? [
          {
            href: '/referrals',
            label: t('nav.referrals'),
            icon: 'referrals',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    // Death and birth registers (docs/plans/phase2-mortuary-and-notifications.md §6).
    ...(can(session, 'deaths.read') || can(session, 'births.read')
      ? [
          {
            href: '/vital-events',
            label: t('nav.deathsAndBirths'),
            icon: 'records',
            group: t('nav.group.frontDesk'),
          } as const,
        ]
      : []),
    // Clinical items stay together so the group heading shows once.
    ...(can(session, 'casualty.register')
      ? [
          {
            href: '/casualty',
            label: t('nav.casualty'),
            icon: 'casualty',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'queues.read')
      ? [
          {
            href: '/queues',
            label: t('nav.queue'),
            icon: 'queue',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'lab.read')
      ? [
          {
            href: '/lab',
            label: t('nav.lab'),
            icon: 'lab',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'imaging.read')
      ? [
          {
            href: '/imaging',
            label: t('nav.imaging'),
            icon: 'imaging',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'inpatient.read')
      ? [
          {
            href: '/wards',
            label: t('nav.wards'),
            icon: 'wards',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'theatre.read')
      ? [
          {
            href: '/theatre',
            label: t('nav.theatre'),
            icon: 'theatre',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    // Maternity: who is in labour (docs/plans/phase2-maternity-mch.md §6).
    ...(can(session, 'maternity.read')
      ? [
          {
            href: '/maternity',
            label: t('nav.maternity'),
            icon: 'maternity',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'mortuary.read')
      ? [
          {
            href: '/mortuary',
            label: t('nav.mortuary'),
            icon: 'mortuary',
            group: t('nav.group.clinical'),
          } as const,
        ]
      : []),
    ...(can(session, 'pharmacy.read')
      ? [
          {
            href: can(session, 'pharmacy.sell') ? '/pharmacy/counter' : '/pharmacy/returns',
            label: t('nav.pharmacy'),
            icon: 'pharmacy',
            group: t('nav.group.pharmacyStores'),
          } as const,
        ]
      : []),
    ...(can(session, 'stock.read')
      ? [
          {
            href: '/stores',
            label: t('nav.stores'),
            icon: 'stores',
            group: t('nav.group.pharmacyStores'),
          } as const,
        ]
      : []),
    ...(can(session, 'procurement.read')
      ? [
          {
            href: '/procurement',
            label: t('nav.procurement'),
            icon: 'approvals',
            group: t('nav.group.pharmacyStores'),
          } as const,
        ]
      : []),
    ...(can(session, 'billing.read')
      ? [
          {
            href: '/cashier',
            label: t('nav.cashier'),
            icon: 'cashier',
            group: t('nav.group.billingClaims'),
          } as const,
        ]
      : []),
    ...(can(session, 'billing.waiver.approve') ||
    can(session, 'billing.credit_note.approve') ||
    can(session, 'payments.refund.approve')
      ? [
          {
            href: '/billing/approvals',
            label: t('nav.approvals'),
            icon: 'approvals',
            group: t('nav.group.billingClaims'),
          } as const,
        ]
      : []),
    ...(can(session, 'claims.read')
      ? [
          {
            href: '/claims',
            label: t('nav.claims'),
            icon: 'claims',
            group: t('nav.group.billingClaims'),
          } as const,
        ]
      : []),
    ...(can(session, 'insurance.claims.read')
      ? [
          {
            href: '/insurance',
            label: t('nav.insurance'),
            icon: 'insurance',
            group: t('nav.group.billingClaims'),
          } as const,
        ]
      : []),
    // Accounting and doctor revenue share (docs/plans/phase3-accounting-and-revenue-share.md §6).
    ...(can(session, 'accounting.read')
      ? [
          {
            href: '/accounting',
            label: 'Accounting',
            icon: 'accounting',
            group: 'Billing & claims',
          } as const,
        ]
      : []),
    ...(can(session, 'revenue_share.read')
      ? [
          {
            href: '/revenue-share',
            label: 'Revenue share',
            icon: 'earnings',
            group: 'Billing & claims',
          } as const,
        ]
      : []),
    ...(canSeeReports(session)
      ? [
          {
            href: '/reports',
            label: t('nav.reports'),
            icon: 'reports',
            group: t('nav.group.reports'),
          } as const,
        ]
      : []),
    // Patient feedback (docs/plans/phase3-portal-feedback-kiswahili.md §7.2).
    ...(can(session, 'feedback.read')
      ? [
          {
            href: '/feedback',
            label: t('nav.feedback'),
            icon: 'records',
            group: t('nav.group.administration'),
          } as const,
        ]
      : []),
    ...(can(session, 'revenue_share.own.read') && !can(session, 'revenue_share.read')
      ? [
          {
            href: '/my-earnings',
            label: 'My earnings',
            icon: 'earnings',
            group: 'Reports',
          } as const,
        ]
      : []),
    // Incident reporting (docs/plans/phase3-operations-modules.md §1.4): anyone who reports.
    ...(can(session, 'incidents.report')
      ? [
          {
            href: '/incidents',
            label: 'Incidents',
            icon: 'incidents',
            group: 'Quality and safety',
          } as const,
        ]
      : []),
    // Infection control (docs/plans/phase3-operations-modules.md §2.4).
    ...(can(session, 'infection.read')
      ? [
          {
            href: '/infection-control',
            label: 'Infection control',
            icon: 'infection',
            group: 'Quality and safety',
          } as const,
        ]
      : []),
    // Kitchen and housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §1.4, §2.4).
    ...(can(session, 'kitchen.read')
      ? [{ href: '/kitchen', label: 'Kitchen', icon: 'kitchen', group: 'Operations' } as const]
      : []),
    ...(can(session, 'housekeeping.read')
      ? [
          {
            href: '/housekeeping',
            label: 'Housekeeping',
            icon: 'housekeeping',
            group: 'Operations',
          } as const,
        ]
      : []),
    // Assets and maintenance (docs/plans/phase3-operations-modules.md §3.3).
    ...(can(session, 'assets.read')
      ? [{ href: '/assets', label: 'Assets', icon: 'assets', group: 'Administration' } as const]
      : []),
    // Roster and attendance (docs/plans/phase3-operations-modules.md §4.3): every staff member.
    ...(can(session, 'roster.read')
      ? [{ href: '/roster', label: 'Roster', icon: 'roster', group: 'Administration' } as const]
      : []),
    // Staff records and licences (docs/plans/phase2-staff-records.md §6).
    ...(can(session, 'staff.read')
      ? [
          {
            href: '/staff',
            label: t('nav.staff'),
            icon: 'staff',
            group: t('nav.group.administration'),
          } as const,
        ]
      : []),
  ];
  // Settings opens on its overview, for anyone who may open at least one settings area.
  const settingsHref = settingsAreasFor(session).length > 0 ? '/settings' : null;
  const footerNavItems: NavItem[] =
    settingsHref === null
      ? []
      : [{ href: settingsHref, label: t('nav.settings'), icon: 'settings' }];

  return (
    <AppShell
      labels={{
        skipToContent: t('shell.skipToContent'),
        menu: t('shell.menu'),
        closeMenu: t('shell.closeMenu'),
        expandSidebar: t('shell.expandSidebar'),
        collapseSidebar: t('shell.collapseSidebar'),
        mainNavigation: t('shell.mainNavigation'),
      }}
      translateTitle={(title) => translatePageTitle(t, title)}
      navItems={navItems}
      footerNavItems={footerNavItems}
      currentPath={location.pathname}
      {...(can(session, 'patients.read')
        ? {
            search: (
              <PatientSearchPalette
                {...(can(session, 'patients.register')
                  ? {
                      registerAction: (text: string, close: () => void) => (
                        <RegisterNewPatientButton searchText={text} onBeforeNavigate={close} />
                      ),
                    }
                  : {})}
              />
            ),
          }
        : {})}
      facility={
        session.facilities.length > 1 ? (
          <MenuButton
            label={session.facility.name}
            aria-label={t('shell.facility', { name: session.facility.name })}
            items={session.facilities.map((f) => ({ id: f.id, label: f.name }))}
            selectedId={session.facility.id}
            onAction={(id) => {
              if (id !== session.facility.id) switchFacility.mutate(id);
            }}
          />
        ) : (
          <strong>{session.facility.name}</strong>
        )
      }
      account={
        <MenuButton
          variant="avatar"
          label={initials(session.user.displayName)}
          aria-label={t('shell.account', { name: session.user.displayName })}
          items={[
            { id: 'account', label: t('shell.accountAndAppearance') },
            { id: 'sign-out', label: t('shell.signOut') },
          ]}
          onAction={(id) => {
            if (id === 'account') void navigate({ to: '/account' });
            if (id === 'sign-out') requestSignOut();
          }}
        />
      }
      statusStrip={
        <>
          <RetryQueueRunner userId={session.user.id} facilityId={session.facility.id} />
          <OfflineStrips />
          <NotificationStrips />
          <MyLicenceStrip />
        </>
      }
    >
      <Outlet />
      {signOutDialog}
    </AppShell>
  );
}
